import { forwardRef, useId, useState } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'

import { AppIcon } from '../app-icon'
import { AllIcons } from '../app-icon/types'

import { BlockSelect } from './components/select-block'
import { Option, SelectProps } from './model'
import s from './styles.module.scss'

export const SelectWithLabel = forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      label,
      placeholder,
      options,
      value,
      title,
      error,
      disabled = false,
      scrollToSelected = false,
      onChange,
      variant = 'default',
    },
    ref,
  ) => {
    const { openDialog, closeDialog } = useDialogBottomSheet()
    const { t } = useTranslation()

    const reactId = useId()
    const inputId = `select-${reactId}`

    const [currentValue, setCurrentValue] = useState<Option | undefined>(() =>
      options?.find((option) => option.value === value),
    )

    const onSelectOption = (option: Option) => {
      closeDialog()
      setCurrentValue(option)
      onChange?.(option)
    }

    const onShowDialogBottomSheet = () => {
      if (disabled) return

      openDialog({
        child: (
          <div className={s.dialog}>
            {title ? <p className={s.title}>{title}</p> : null}

            <BlockSelect
              value={currentValue}
              listOption={options}
              scrollToSelected={scrollToSelected}
              onChange={onSelectOption}
            />
          </div>
        ),
      })
    }

    return (
      <div className={s.root}>
        <div
          ref={ref}
          className={clsx(
            s.container,
            s[`variant-${variant}`],
            error && s.error,
            disabled && s.disabled,
          )}
          onClick={onShowDialogBottomSheet}
        >
          <input
            id={inputId}
            value={currentValue ? t(currentValue.name) : ''}
            placeholder=" "
            readOnly
            className={s.fakeInput}
          />

          {label && (
            <label
              htmlFor={inputId}
              className={s.label}
            >
              {label}
            </label>
          )}

          <div className={s.valueText}>
            {currentValue ? t(currentValue.name) : placeholder}
          </div>

          <AppIcon
            id={AllIcons.ARROW_BOTTOM}
            className={s.arrow}
            height={24}
            width={24}
          />
        </div>

        {typeof error !== 'boolean' && error?.message && (
          <p className={s.errorText}>{error.message}</p>
        )}
      </div>
    )
  },
)

SelectWithLabel.displayName = 'SelectWithLabel'
